<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-100" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-100 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <form @submit.prevent="submit" class="flex flex-col divide-y divide-gray-200 bg-white">
                                <div class="px-4 py-4 sm:px-6">
                                    <div class="flex items-start justify-between">
                                        <div class="text-left">
                                            <DialogTitle class="text-base font-semibold text-gray-900">{{ $t('create_gallery.title') }}</DialogTitle>
                                            <p class="mt-1 text-sm text-gray-500">{{ $t('create_gallery.text') }}</p>
                                        </div>
                                        <button type="button" class="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:ring-2 focus-visible:ring-royal-blue-500 focus:outline-hidden" @click="close">
                                            <span class="absolute -inset-2.5" />
                                            <span class="sr-only">{{ $t('create_gallery.close') }}</span>
                                            <XMarkIcon class="size-6" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>

                                <div class="px-4 py-5 sm:px-6 space-y-6">
                                    <div>
                                        <label for="name" class="block text-sm/6 font-medium text-gray-900">{{ $t('create_gallery.name') }}</label>
                                        <div class="mt-2 grid grid-cols-1">
                                            <input
                                                id="name"
                                                name="name"
                                                v-model="form.name"
                                                type="text"
                                                autocomplete="name"
                                                :class="{
                                                    'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                                    'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('name')
                                                }" />
                                            <ExclamationCircleIcon v-if="hasError('name')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                        </div>
                                        <p class="mt-2 text-sm text-red-600 text-left" id="name-error" v-if="hasError('name')">{{ getError('name') }}</p>
                                    </div>

                                    <div>
                                        <label for="date" class="block text-sm/6 font-medium text-gray-900">{{ $t('create_gallery.date') }}</label>
                                        <div class="mt-2">
                                            <DatePickerComponent
                                                id="date"
                                                v-model="form.date"
                                                :error="hasError('date')"
                                                :placeholder="$t('create_gallery.date')"
                                            />
                                        </div>
                                        <p class="mt-2 text-sm text-red-600 text-left" id="date-error" v-if="hasError('date')">{{ getError('date') }}</p>
                                    </div>

                                    <div>
                                        <Listbox as="div" v-model="form.storage_period">
                                            <ListboxLabel class="block text-sm/6 font-medium text-gray-900">{{ $t('create_gallery.storage_period') }}</ListboxLabel>
                                            <div class="relative mt-2">
                                                <ListboxButton
                                                    :class="[
                                                        'grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 sm:text-sm/6',
                                                        hasError('storage_period') ? 'outline-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600' : 'outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600'
                                                    ]"
                                                >
                                                    <span class="col-start-1 row-start-1 flex items-baseline gap-2 truncate pr-6">
                                                        <span class="truncate">{{ selectedStoragePeriodLabel }}</span>
                                                        <span v-if="storagePeriodUntil(form.storage_period)" class="text-xs text-gray-500 truncate">{{ storagePeriodUntil(form.storage_period) }}</span>
                                                    </span>
                                                    <ChevronUpDownIcon class="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                                                </ListboxButton>

                                                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                                    <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
                                                        <ListboxOption as="template" v-for="period in storage_periods" :key="`storage-period-option-${period.value}`" :value="period.value" v-slot="{ active, selected }">
                                                            <li :class="[active ? 'bg-royal-blue-600 text-white outline-hidden' : 'text-gray-900', 'relative cursor-default py-2 pr-9 pl-3 select-none']">
                                                                <span class="flex items-baseline gap-2">
                                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'truncate']">{{ period.label }}</span>
                                                                    <span v-if="storagePeriodUntil(period.value)" :class="[active ? 'text-white/80' : 'text-gray-500', 'text-xs truncate']">{{ storagePeriodUntil(period.value) }}</span>
                                                                </span>
                                                                <span v-if="selected" :class="[active ? 'text-white' : 'text-royal-blue-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                                    <CheckIcon class="size-5" aria-hidden="true" />
                                                                </span>
                                                            </li>
                                                        </ListboxOption>
                                                    </ListboxOptions>
                                                </transition>
                                            </div>
                                        </Listbox>
                                        <p class="mt-2 text-sm text-red-600 text-left" id="storage_period-error" v-if="hasError('storage_period')">{{ getError('storage_period') }}</p>
                                    </div>

                                    <div v-if="galleries.length">
                                        <Listbox as="div" v-model="form.copy_gallery_id">
                                            <ListboxLabel class="block text-sm/6 font-medium text-gray-900">{{ $t('create_gallery.copy_gallery') }}</ListboxLabel>
                                            <div class="relative mt-2">
                                                <ListboxButton
                                                    :class="[
                                                        'grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 sm:text-sm/6',
                                                        hasError('copy_gallery_id') ? 'outline-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600' : 'outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600'
                                                    ]"
                                                >
                                                    <span class="col-start-1 row-start-1 flex items-center gap-2 pr-6">
                                                        <span v-if="selectedCopyGallery" class="size-6 shrink-0 overflow-hidden rounded bg-gray-100">
                                                            <img v-if="selectedCopyGallery.small_cover_url" :src="selectedCopyGallery.small_cover_url" :alt="selectedCopyGallery.name" class="size-full object-cover" />
                                                        </span>
                                                        <span class="truncate">{{ selectedCopyGallery ? selectedCopyGallery.name : $t('create_gallery.copy_gallery_no') }}</span>
                                                    </span>
                                                    <ChevronUpDownIcon class="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                                                </ListboxButton>

                                                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                                    <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
                                                        <ListboxOption as="template" :value="null" v-slot="{ active, selected }">
                                                            <li :class="[active ? 'bg-royal-blue-600 text-white outline-hidden' : 'text-gray-900', 'relative cursor-default py-2 pr-9 pl-3 select-none']">
                                                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ $t('create_gallery.copy_gallery_no') }}</span>
                                                                <span v-if="selected" :class="[active ? 'text-white' : 'text-royal-blue-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                                    <CheckIcon class="size-5" aria-hidden="true" />
                                                                </span>
                                                            </li>
                                                        </ListboxOption>

                                                        <ListboxOption as="template" v-for="gallery in galleries" :key="`gallery-copy-item-select-${gallery.id}`" :value="gallery.id" v-slot="{ active, selected }">
                                                            <li :class="[active ? 'bg-royal-blue-600 text-white outline-hidden' : 'text-gray-900', 'relative cursor-default py-2 pr-9 pl-3 select-none']">
                                                                <span class="flex items-center gap-2">
                                                                    <span :class="[active ? 'bg-white/20' : 'bg-gray-100', 'size-6 shrink-0 overflow-hidden rounded']">
                                                                        <img v-if="gallery.small_cover_url" :src="gallery.small_cover_url" :alt="gallery.name" class="size-full object-cover" />
                                                                    </span>
                                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ gallery.name }}</span>
                                                                </span>
                                                                <span v-if="selected" :class="[active ? 'text-white' : 'text-royal-blue-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                                    <CheckIcon class="size-5" aria-hidden="true" />
                                                                </span>
                                                            </li>
                                                        </ListboxOption>
                                                    </ListboxOptions>
                                                </transition>
                                            </div>
                                        </Listbox>
                                        <p class="mt-2 text-sm text-red-600 text-left" id="copy_gallery_id-error" v-if="hasError('copy_gallery_id')">{{ getError('copy_gallery_id') }}</p>

                                        <div class="mt-4 flex text-sm">
                                            <p class="group inline-flex items-start text-gray-500">
                                                <QuestionMarkCircleIcon class="size-5 text-gray-400 shrink-0" aria-hidden="true" />
                                                <span class="ml-2 text-left">{{ $t('create_gallery.copy_gallery_info') }}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex shrink-0 justify-end px-4 py-4 bg-gray-50">
                                    <button type="button" class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 cursor-pointer" @click="close">
                                        {{ $t('create_gallery.cancel_button') }}
                                    </button>
                                    <button type="submit" :disabled="isCreating" :class="{'ml-4 inline-flex justify-center items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-600': true, 'bg-royal-blue-600 hover:bg-royal-blue-700 cursor-pointer': !isCreating, 'bg-royal-blue-400 cursor-not-allowed': isCreating}">
                                        <svg v-if="isCreating" class="animate-spin size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {{ $t('create_gallery.create_button') }}
                                    </button>
                                </div>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'
import {mapActions, mapGetters} from "vuex";
import {validationMethods} from "@/_helpers/validation";
import {create} from "@/data/requests/gallery";
import {ResponseError} from "@/types";
import {CheckIcon, ChevronUpDownIcon, ExclamationCircleIcon} from "@heroicons/vue/16/solid";
import DatePickerComponent from "@/components/UI/DatePickerComponent.vue";
import moment from "moment";

export default defineComponent({
    name: 'CreateGalleryComponent',
    components: {
        DatePickerComponent,
        CheckIcon,
        ChevronUpDownIcon,
        ExclamationCircleIcon,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XMarkIcon,
        QuestionMarkCircleIcon,
        Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions
    },

    props: {
        open: {
            type: Boolean,
            required: true,
        },
    },

    data: function () {
        return {
            isCreating: false,
            isFetchingGalleries: false,

            storage_periods: [
                {
                    value: "0",
                    label: this.$t('create_gallery.storage_periods.0'),
                },
                {
                    value: "7d",
                    label: this.$t('create_gallery.storage_periods.7d'),
                },
                {
                    value: "30d",
                    label: this.$t('create_gallery.storage_periods.30d'),
                },
                {
                    value: "90d",
                    label: this.$t('create_gallery.storage_periods.90d'),
                },
                {
                    value: "365d",
                    label: this.$t('create_gallery.storage_periods.365d'),
                },
            ],

            form: {
                name: "",
                date: "",
                storage_period: "0",
                copy_gallery_id: null
            } as { name: string, date: string | null, storage_period: string | null, copy_gallery_id: string | null },

            errors: {} as { [key: string]: string[] },
        }
    },

    watch: {
        open: {
            handler(newValue) {
                if(newValue) {
                    this.fetchGalleries();
                }
            },
            immediate: true
        },
        'form.name': function() {
            delete this.errors['name'];
        },
        'form.date': function() {
            delete this.errors['date'];
        },
        'form.storage_period': function() {
            delete this.errors['storage_period'];
        },
        'form.copy_gallery_id': function() {
            delete this.errors['copy_gallery_id'];
        },
    },

    computed: {
        ...mapGetters({
            galleries: "galleries/galleriesList",
        }),

        selectedStoragePeriodLabel(): string {
            const found = this.storage_periods.find(p => p.value === this.form.storage_period);
            return found ? found.label : '';
        },

        selectedCopyGallery(): any | null {
            if(!this.form.copy_gallery_id) {
                return null;
            }
            return (this.galleries as any[]).find(g => g.id === this.form.copy_gallery_id) || null;
        }
    },

    methods: {
        close() {
            this.$emit('close');
        },

        ...mapActions({
            loadGalleries: "galleries/loadGalleries",
            loadGalleriesList: "galleries/loadGalleriesList",

            errorAlert: "alerts/error"
        }),

        ...validationMethods(),

        storagePeriodUntil(value: string | null): string {
            if(!value) {
                return '';
            }
            const match = /^(\d+)d$/.exec(value);
            if(!match) {
                return '';
            }
            const days = parseInt(match[1], 10);
            const locale = (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'uk';
            return this.$t('create_gallery.storage_period_until', {
                date: moment().add(days, 'days').locale(locale).format('D MMMM YYYY')
            });
        },

        fetchGalleries() {
            if(this.galleries.length || this.isFetchingGalleries) {
                return;
            }

            this.isFetchingGalleries = true;

            this.loadGalleriesList()
                .then(() => {
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('create_gallery.gallery_loading_error', {
                            error: data.message
                        }),
                    });
                })
                .finally(() => {
                    this.isFetchingGalleries = false;
                });
        },

        submit() {
            this.isCreating = true;

            create(this.form, (gallery) => {
                this.isCreating = false;

                this.loadGalleries();
                this.loadGalleriesList();

                this.$router.push(`/galleries/${gallery}/photos`);

                this.errors = {};
                this.form = {
                    name: "",
                    date: "",
                    storage_period: "0",
                    copy_gallery_id: null
                };

                this.close();
            }, (type, data) => {
                this.isCreating = false;

                if(type == ResponseError.Validation) {
                    this.errors = data.data;

                    this.errorAlert({
                        message: this.$t('create_gallery.validation_error'),
                    });
                } else {
                    this.errorAlert({
                        message: data.message,
                    });
                }
            });
        }
    }
});
</script>
